import Action from '@/gf/components/Action'
import MagicIcon from '@/gf/components/MagicIcon'
import Modal from '@/gf/components/ModalNext'
import Spinner from '@/gf/components/Spinner'
import { ExclamationCircleIcon } from '@heroicons/react/24/outline'
import { UploadStage } from './DocumentUploadProvider'
import useQuoteUpload from './useQuoteUpload'

const Processing = () => (
  <div className="flex justify-center items-center py-12">
    <div className="flex flex-col gap-y-2 text-center max-w-md">
      <h4 className="text-2xl font-medium">Processing Quote File</h4>

      <Spinner />

      <p className="text-lg">
        Most quotes finish in 10–30 seconds. Long or scanned PDFs can take a couple of minutes —
        please don't navigate away.
      </p>
    </div>
  </div>
)

const Error = ({
  error,
  onClose,
  onRetry,
}: {
  error: string | null
  onClose: () => void
  onRetry?: () => void
}) => (
  <div className="flex flex-col items-center py-12">
    <div className="flex flex-col gap-y-6 text-center max-w-md">
      <h4 className="text-2xl font-medium">Something went wrong</h4>

      <p className="flex text-lg text-rose-600 gap-x-4 items-center text-left">
        <ExclamationCircleIcon className="w-6 h-6 flex shrink-0 text-rose-600" />
        {error}
      </p>
    </div>

    <div className="w-full flex justify-center mt-6 gap-x-3">
      {onRetry && (
        <Action.P onClick={onRetry} className="w-1/2">
          Try Again
        </Action.P>
      )}
      <Action.S onClick={onClose} className={onRetry ? 'w-1/4' : 'w-3/4'}>
        Close
      </Action.S>
    </div>
  </div>
)

// Build the manual-add prompt to omit fields the extractor populated. `null`
// means the extractor did not find that field on the document, so the dealer
// still has to enter it. `0` (or any number) means we already populated the
// form and the dealer only needs to tweak it.
const buildManualAddCopy = (shippingPopulated: boolean, taxPopulated: boolean): string => {
  const fields = ['the line item availability']
  if (!shippingPopulated) fields.push('shipping cost')
  if (!taxPopulated) fields.push('sales tax')

  const list =
    fields.length === 1
      ? fields[0]
      : fields.length === 2
        ? `${fields[0]} and ${fields[1]}`
        : `${fields.slice(0, -1).join(', ')}, and ${fields[fields.length - 1]}`

  return `If applicable, please add ${list} manually. Review the quote details and be sure to click submit.`
}

const Finished = ({ onClose }: { onClose: () => void }) => {
  const { shippingCost, taxCost } = useQuoteUpload()
  const copy = buildManualAddCopy(shippingCost !== null, taxCost !== null)

  return (
    <div className="flex flex-col items-center p-8">
      <div className="flex flex-col gap-y-2 text-center">
        <h4 className="text-2xl font-medium">
          <MagicIcon className="h-8 inline-block" /> Quote uploaded!
        </h4>

        <p className="text-lg">{copy}</p>
      </div>

      <div className="w-full flex justify-center mt-6">
        <Action.P onClick={onClose} className="w-3/4">
          Got it!
        </Action.P>
      </div>
    </div>
  )
}

const QuoteUploadModal = ({
  uploadStage,
  error,
  onClose,
  onRetry,
}: {
  uploadStage: UploadStage
  error: string | null
  onClose: () => void
  onRetry?: () => void
}) =>
  uploadStage ? (
    <Modal open onClose={onClose}>
      {uploadStage === 'processing' && <Processing />}
      {uploadStage === 'error' && <Error error={error} onClose={onClose} onRetry={onRetry} />}
      {uploadStage === 'finished' && <Finished onClose={onClose} />}
    </Modal>
  ) : null

export default QuoteUploadModal
